import React from 'react';
import { useUploadStore } from '@/store/uploadStore';
import { X, CheckCircle, AlertCircle, File, Loader2, Minus, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

export const GlobalUploadManager: React.FC = () => {
    const { uploads, removeUpload, clearCompleted } = useUploadStore();
    const [isMinimized, setIsMinimized] = useState(false);
    
    const uploadList = Object.values(uploads);
    if (uploadList.length === 0) return null;

    const activeUploads = uploadList.filter(u => u.status === 'uploading').length;
    const completedUploads = uploadList.filter(u => u.status === 'success').length;
    const errorUploads = uploadList.filter(u => u.status === 'error').length;

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="fixed bottom-4 right-4 z-[100] w-80 bg-background border rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[70vh]">
            {/* Header */}
            <div className="bg-muted px-4 py-3 flex items-center justify-between border-b">
                <div className="flex flex-col">
                    <span className="text-sm font-semibold">
                        {activeUploads > 0 ? `Uploads en cours (${activeUploads})` : 'Uploads terminés'}
                    </span>
                    {(completedUploads > 0 || errorUploads > 0) && activeUploads === 0 && (
                        <button onClick={clearCompleted} className="text-xs text-primary hover:underline text-left mt-0.5">
                            Effacer terminés
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button 
                        onClick={() => setIsMinimized(!isMinimized)} 
                        className="p-1 hover:bg-black/5 dark:hover:bg-white/10 rounded"
                    >
                        {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                    </button>
                    {activeUploads === 0 && (
                        <button 
                            onClick={clearCompleted} 
                            className="p-1 hover:bg-black/5 dark:hover:bg-white/10 rounded text-muted-foreground"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            {!isMinimized && (
                <div className="overflow-y-auto p-2 space-y-2">
                    {uploadList.map((upload) => (
                        <div key={upload.id} className="p-2 border rounded-md bg-card flex flex-col gap-2">
                            <div className="flex items-start gap-3">
                                <div className="mt-1">
                                    {upload.status === 'uploading' ? (
                                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                                    ) : upload.status === 'success' ? (
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-destructive" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate" title={upload.fileName}>
                                        {upload.fileName}
                                    </p>
                                    <div className="flex items-center justify-between mt-1">
                                        <p className="text-xs text-muted-foreground">
                                            {formatSize(upload.fileSize)}
                                        </p>
                                        <p className="text-xs font-medium">
                                            {Math.round(upload.progress)}%
                                        </p>
                                    </div>
                                </div>
                                {(upload.status === 'success' || upload.status === 'error') && (
                                    <button 
                                        onClick={() => removeUpload(upload.id)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            
                            {/* Progress bar */}
                            {upload.status === 'uploading' && (
                                <div className="w-full bg-secondary h-1.5 rounded-full overflow-hidden">
                                    <div 
                                        className="bg-primary h-full transition-all duration-300" 
                                        style={{ width: `${upload.progress}%` }}
                                    />
                                </div>
                            )}

                            {/* Error message */}
                            {upload.status === 'error' && upload.error && (
                                <p className="text-xs text-destructive mt-1">
                                    {upload.error}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
